<template>
  <div class="audience-cta-wrapper" :aria-busy="loading ? 'true' : null">
    <div class="ds-footer" :class="{ 'ds-footer--loading': loading }">
      <p class="ds-footer__title">
        {{ $t('thank_for_participation') }}
      </p>
      <p v-if="audienceSignUpInvitationCtaVariant && ctaSubtitle" class="ds-footer__subtitle">
        {{ ctaSubtitle }}
      </p>

      <div v-if="audienceSignUpInvitationCtaVariant && isShowAhaSection" class="ds-footer__section">
        <div class="ds-footer__slot">
          <ds-button
            variant="primary"
            size="touch"
            block
            data-testid="audience-cta-get-ahaslides-button"
            @click="openTrialLink"
          >
            <span slot="icon" class="ds-footer__logo"><img :src="splashWhiteUrl" alt=""></span>
            <span class="ds-footer__label">{{ ctaButtonLabel }}</span>
          </ds-button>
        </div>
        <referral-free-signup-note v-if="isReferralActive" />
      </div>

      <div
        v-if="isShownCTAButtons"
        class="ds-footer__actions"
        :class="audienceSignUpInvitationCtaVariant && isShowAhaSection ? 'ds-footer__next-section' : 'ds-footer__section'"
      >
        <div class="ds-footer__slot ds-footer__action--wide" v-if="retake">
          <ds-button
            variant="secondary-deck"
            size="touch"
            block
            data-testid="audience-footer-retake-button"
            @click="startRetake('footer')"
          >
            <span slot="icon"><ds-icon name="system-arrow-counter-clockwise" size="16" decorative /></span>
            {{ $t('replayPractiseAgainButton') }}
          </ds-button>
        </div>
        <div class="ds-footer__slot" v-if="isEnableSlideReview">
          <ds-button
            variant="secondary-deck"
            size="touch"
            block
            data-testid="audience-footer-review-slides-button"
            @click="openTabLookback"
          >
            <span slot="icon"><ds-icon name="system-presentation-chart" size="16" decorative /></span>
            {{ $t('review_slides') }}
          </ds-button>
        </div>
        <div class="ds-footer__slot" v-if="isEnableFeedback">
          <ds-button
            variant="secondary-deck"
            size="touch"
            block
            :disabled="isDisabledFeedbackButton"
            data-testid="audience-footer-give-feedback-button"
            @click="openTabFeedback"
          >
            <span slot="icon"><ds-icon name="system-chat-text" size="16" decorative /></span>
            {{ $t('give_feedback') }}
          </ds-button>
        </div>
      </div>

      <div v-if="!audienceSignUpInvitationCtaVariant && isShowAhaSection" class="ds-footer__section">
        <div class="ds-footer__slot">
          <ds-button
            variant="tertiary-deck"
            size="touch"
            block
            data-testid="audience-footer-get-ahaslides-free-button"
            @click="openTrialLink"
          >
            <span slot="icon" class="ds-footer__logo"><img :src="splashUrl" alt=""></span>
            <span class="ds-footer__label">{{ ctaButtonLabel }}</span>
          </ds-button>
        </div>
        <referral-free-signup-note v-if="isReferralActive" />
      </div>
    </div>
  </div>
</template>

<script>
import { designSystemLogoUrl } from '../../utils/designSystemLoader'
import DsButton from '../replay/RetakeDsButton'
import AudienceCTA from './AudienceCTA.vue'

// The storybook kit registers a global AhaIcon, which would otherwise swallow the DS <aha-icon> tag.
const DsIcon = {
  functional: true,
  render: (h, { data }) => h('aha-icon', { ...data, pre: true }),
}

export default {
  components: { DsButton, DsIcon },
  extends: AudienceCTA,
  props: {
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      splashUrl: designSystemLogoUrl('thesplash.svg'),
      splashWhiteUrl: designSystemLogoUrl('thesplash-white.svg'),
    }
  },
}
</script>

<style lang="scss" scoped>
body.is-audience-paced {
  .audience-cta-wrapper {
    padding-bottom: var(--h-audience-paced);
  }
}

.audience-cta-wrapper {
  user-select: none;
  background-color: var(--aha-bg-layout, #F7F7F7);
  color: var(--aha-text-default, #1A1A1A);
  text-align: center;
}

.ds-footer {
  max-width: 600px;
  margin: 0 auto;
  padding: var(--aha-space-24, 24px) var(--aha-space-16, 16px);
}

.ds-footer__title {
  margin: 0;
  font-size: var(--aha-size-h6, 18px);
  font-weight: var(--aha-weight-semibold, 600);
}

.ds-footer__subtitle {
  margin: var(--aha-space-8, 8px) 0 0;
  font-size: var(--aha-size-l, 16px);
  color: var(--aha-text-secondary, #4A4A4A);
}

.ds-footer__section {
  margin-top: var(--aha-space-16, 16px);
}

.ds-footer__next-section {
  margin-top: var(--aha-space-24, 24px);
}

.ds-footer__logo {
  display: inline-flex;

  img {
    width: 100%;
    height: 100%;
  }
}

.ds-footer__actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--aha-space-8, 8px);

  .ds-footer__action--wide,
  .ds-footer__action--wide + :last-child {
    grid-column: 1 / -1;
  }
}

.ds-footer__slot {
  min-width: 0;
}

// The DS button sets white-space: nowrap on its inner button; an explicit value on our own slotted span lets a long label wrap inside the fixed 48px box.
.ds-footer__label {
  min-width: 0;
  white-space: normal;
  line-height: 1.15;
}

// Until the DS script defines aha-button, each slot holds the button's final box and shows its label.
.ds-footer--loading .ds-footer__slot {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 48px;
  padding: 0 var(--aha-space-24, 24px);
  font-weight: var(--aha-weight-semibold, 600);
  border: 1px solid var(--aha-border-default, #E3E3E3);
  border-radius: 8px;
  background-color: var(--aha-bg-container, #FFFFFF);
  font-size: var(--aha-size-l, 16px);
  pointer-events: none;

  ::v-deep [slot='icon'] {
    display: inline-flex;
    width: 16px;
    height: 16px;
    margin-right: var(--aha-space-8, 8px);
    vertical-align: middle;
  }
}

::v-deep .referral-free-signup-note {
  font-size: var(--aha-size-default, 14px);
  color: var(--aha-text-secondary, #4A4A4A);
}
</style>
